import React, { useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import AppTooltip from './AppTooltip';
import InfoIcon from '@mui/icons-material/Info';

import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import JsonTableRenderer from './JsonTableRenderer';

const primaryColor = '#5757b0';

const TagTooltip = ({
  children,
  data,
  dataType = 'auto',
  customStyles = {},
  contentRenderer = null,
  showActions = false,
  openOnClick = false,
  forceTooltip = false,
  // Action handlers - generic callbacks
  onShowChart = null,
  onShowInfo = null,
  onDelete = null,
  onShowJsonViewer = null, // New prop for JSON viewer
  onImageClick = null,
  onEdit = null,
  extraContent = null,
  onClose = null,
  tooltipPlacement = 'top',
  // Optional props for display
  title = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [frozenAnchor, setFrozenAnchor] = useState(null);
  const textData = data === null || data === undefined ? '' : String(data);

  const closeTooltip = () => {
    setIsOpen(false);
    setFrozenAnchor(null);
    if (onClose) {
      onClose();
    }
  };

  const handleShowChart = () => {
    if (onShowChart) {
      onShowChart();
    }
    closeTooltip();
  };

  const handleShowInfo = () => {
    if (onShowInfo) {
      onShowInfo();
    }
    closeTooltip();
  };

  const handleShowJsonViewer = () => {
    if (onShowJsonViewer) {
      onShowJsonViewer();
    }
    closeTooltip();
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
  };

  const handleManualClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    closeTooltip();
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete();
    }
    closeTooltip();
  };

  const isJsonString = (str) => {
    if (typeof str !== 'string') return false;
    try {
      const json = JSON.parse(str);
      return (typeof json === 'object' && json !== null) || Array.isArray(json);
    } catch (e) {
      return false;
    }
  };

  const isBase64Image = (str) => {
    if (typeof str !== 'string') return false;
    // Check for data URL format
    if (str.startsWith('data:image/')) {
      return true;
    }
    // Check for pure base64 that could be an image (common patterns)
    if (str.length > 100 && /^[A-Za-z0-9+/]+={0,2}$/.test(str)) {
      // Additional heuristic: base64 images are typically quite long
      return str.length > 1000;
    }
    return false;
  };

  const formatJsonForDisplay = (jsonData) => {
    try {
      if (typeof jsonData === 'object' && jsonData !== null) {
        return { isJson: true, data: jsonData };
      }
      if (typeof jsonData === 'string') {
        const parsed = JSON.parse(jsonData);
        if ((typeof parsed !== 'object' && !Array.isArray(parsed)) || parsed === null) {
          return { isJson: false, data: jsonData };
        }
        return { isJson: true, data: parsed };
      }
      return { isJson: false, data: jsonData };
    } catch (e) {
      return { isJson: false, data: jsonData };
    }
  };

  const actualDataType = dataType === 'auto' ?
    (isBase64Image(data) ? 'image' :
      (isJsonString(data) || (typeof data === 'object' && data !== null) ? 'json' : 'text')) :
    (dataType === 'text' && (isJsonString(data) || (typeof data === 'object' && data !== null)) ? 'json' : dataType);

  const formattedData = actualDataType === 'json' ?
    formatJsonForDisplay(data) : { isJson: false, data };

  // Check if there's no meaningful data to display
  const hasNoData = () => {
    if (!data) return true;
    if (data === '') return true;
    if (data === '(no data)') return true;
    if (data === 'null' || data === 'undefined') return true;
    if (typeof data === 'string' && data.trim() === '') return true;
    if (formattedData.isJson && (!formattedData.data ||
      (Array.isArray(formattedData.data) && formattedData.data.length === 0) ||
      (typeof formattedData.data === 'object' && Object.keys(formattedData.data).length === 0))) return true;
    return false;
  };

  // If there's no data, just return the children without tooltip
  if (!forceTooltip && hasNoData()) {
    return <span>{children}</span>;
  }

  const handleTooltipClose = (event, reason) => {
    if (openOnClick && reason && reason !== 'escapeKeyDown') {
      return;
    }
    setIsOpen(false);
    setFrozenAnchor(null);
    if (onClose) {
      onClose();
    }
  };

  const handleTriggerClick = (event) => {
    if (!openOnClick) return;
    if (event?.target?.closest?.('[data-tag-tooltip-ignore="true"]')) {
      return;
    }
    const nextOpen = !isOpen;
    if (nextOpen) {
      const anchorRect = event.currentTarget.getBoundingClientRect();
      setFrozenAnchor({
        contextElement: event.currentTarget,
        getBoundingClientRect: () => anchorRect,
      });
    } else {
      setFrozenAnchor(null);
      if (onClose) {
        onClose();
      }
    }
    setIsOpen(nextOpen);
  };

  const renderTooltipContent = () => {
    const imageSrc = actualDataType === 'image' && typeof data === 'string'
      ? (data.startsWith('data:') ? data : `data:image/png;base64,${data}`)
      : null;

    return (
      <Box sx={{
        maxWidth: 'min(800px, calc(100vw - 24px))',
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        borderRadius: '12px',
        border: `2px solid ${primaryColor}20`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        ...customStyles
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2.5,
          py: 1.5,
          pb: 1,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          borderBottom: `2px solid ${primaryColor}15`,
          minHeight: '32px' // Ensure consistent height
        }}>
          {/* Title section - flex with ellipsis */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: '1 1 auto',
            minWidth: 0 // Allow shrinking
          }}>
            <Typography variant="body2" sx={{
              fontWeight: '700',
              color: primaryColor,
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: '0 1 auto',
              minWidth: 0
            }}>
              {title || (actualDataType === 'image' ? 'Image Preview' :
                formattedData.isJson ? 'Data Preview' : 'Data')}
            </Typography>
            {formattedData.isJson && (
              <Box sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: `${primaryColor}15`,
                borderRadius: '20px',
                flexShrink: 0 // Don't shrink the badge
              }}>
                <Typography variant="caption" sx={{
                  color: primaryColor,
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}>
                  {Array.isArray(formattedData.data) ?
                    `${formattedData.data.length} items` :
                    `${Object.keys(formattedData.data || {}).length} fields`
                  }
                </Typography>
              </Box>
            )}
          </Box>

          {/* Action buttons - always visible on the right */}
          {(showActions || openOnClick) && (
            <Box sx={{
              display: 'flex',
              gap: 0.5,
              flexShrink: 0, // Never shrink the action buttons
              alignItems: 'center'
            }}>
              {showActions && (
                <>
                  {onShowChart && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowChart();
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        color: primaryColor,
                        '&:hover': {
                          backgroundColor: `${primaryColor}15`,
                        }
                      }}
                      title="Show Chart"
                    >
                      <BarChartIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  {onShowInfo && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowInfo();
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        color: primaryColor,
                        '&:hover': {
                          backgroundColor: `${primaryColor}15`,
                        }
                      }}
                      title="Show Info"
                    >
                      <InfoIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  {onEdit && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit();
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        color: primaryColor,
                        '&:hover': {
                          backgroundColor: `${primaryColor}15`,
                        }
                      }}
                      title="Edit"
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick();
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#d32f2f15',
                        }
                      }}
                      title="Delete"
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </>
              )}
              {openOnClick && (
                <IconButton
                  size="small"
                  onClick={handleManualClose}
                  sx={{
                    width: 24,
                    height: 24,
                    color: '#666',
                    '&:hover': {
                      backgroundColor: '#00000010',
                    }
                  }}
                  title="Close"
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2.5,
        }}>
          {contentRenderer ? (
            contentRenderer
          ) : actualDataType === 'image' ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 2
            }}>
              <img
                src={imageSrc}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 220px)',
                  borderRadius: '8px',
                  border: `1px solid ${primaryColor}20`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  cursor: onImageClick ? 'pointer' : 'default'
                }}
                onClick={() => {
                  if (onImageClick && imageSrc) {
                    onImageClick(imageSrc);
                  }
                }}
                onError={(e) => {
                  // Fallback to text display if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <Box sx={{
                display: 'none', // Hidden by default, shown if image fails
                backgroundColor: '#fff',
                padding: '12px 16px',
                borderRadius: '8px',
                border: `1px solid ${primaryColor}20`,
                width: '100%'
              }}>
                <Typography variant="caption" sx={{
                  wordBreak: 'break-word',
                  color: '#666',
                  fontSize: '0.7rem',
                  fontStyle: 'italic'
                }}>
                  Invalid image data
                </Typography>
                <Typography variant="caption" sx={{
                  wordBreak: 'break-word',
                  color: '#333',
                  fontSize: '0.7rem',
                  lineHeight: 1.5,
                  display: 'block',
                  mt: 1
                }}>
                  {String(data).substring(0, 200)}...
                </Typography>
              </Box>
            </Box>
          ) : formattedData.isJson ? (
            Array.isArray(formattedData.data) ? (
              <JsonTableRenderer
                data={formattedData.data}
                maxItems={Infinity}
                showIndex={true}
                compact={true}
                disableInternalScroll={true}
              />
            ) : (
              <JsonTableRenderer
                data={formattedData.data}
                maxItems={Infinity}
                showIndex={false}
                compact={true}
                disableInternalScroll={true}
              />
            )
          ) : (
            <Box sx={{
              backgroundColor: '#fff',
              padding: '12px 16px',
              borderRadius: '8px',
              border: `1px solid ${primaryColor}20`,
              width: '100%'
            }}>
              <Typography variant="caption" component="pre" sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                color: '#333',
                fontSize: '0.75rem',
                lineHeight: 1.5,
                fontFamily: 'inherit'
              }}>
                {textData}
              </Typography>
            </Box>
          )}
        </Box>

        {(onShowJsonViewer && formattedData.isJson) || extraContent ? (
          <Box sx={{
            flexShrink: 0,
            px: 2.5,
            pb: 2.5,
            pt: 1.5,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            borderTop: `1px solid ${primaryColor}10`
          }}>
            {onShowJsonViewer && formattedData.isJson && (
              <Button
                size="small"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowJsonViewer();
                }}
                startIcon={<DataObjectIcon sx={{ fontSize: 16 }} />}
                fullWidth
                sx={{
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  backgroundColor: primaryColor,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: `${primaryColor}CC`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
              >
                View All Data
              </Button>
            )}

            {extraContent && (
              <Box sx={{
                mt: onShowJsonViewer && formattedData.isJson ? 1.5 : 0,
                pt: onShowJsonViewer && formattedData.isJson ? 1.5 : 0,
                borderTop: onShowJsonViewer && formattedData.isJson ? `1px dashed ${primaryColor}15` : 'none'
              }}>
                {extraContent}
              </Box>
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <>
      <AppTooltip
        title={renderTooltipContent()}
        placement={tooltipPlacement}
        arrow
        noBackground={true}
        PopperProps={{
          anchorEl: frozenAnchor || undefined,
          popperOptions: openOnClick ? { strategy: 'fixed' } : undefined,
          modifiers: [
            ...(openOnClick ? [{
              name: 'eventListeners',
              options: {
                // A click-opened tag detail should remain stable while the
                // evidence viewer scrolls or recenters itself.
                scroll: false,
                resize: false,
              },
            }] : []),
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
                altAxis: true
              }
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom', 'top', 'right', 'left']
              }
            }
          ]
        }}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'transparent',
              maxWidth: 'calc(100vw - 12px)',
              '& .MuiTooltip-arrow': {
                color: primaryColor,
              },
            },
          },
        }}
        {...(openOnClick
          ? {
              open: isOpen,
              onClose: handleTooltipClose,
              TransitionProps: { timeout: 0 },
              disableHoverListener: true,
              disableFocusListener: true,
              disableTouchListener: true,
            }
          : {})}
      >
        <span onClickCapture={openOnClick ? handleTriggerClick : undefined}>{children}</span>
      </AppTooltip>
    </>
  );
};

export default TagTooltip;
